import type {
  CatalogItem,
  CatalogSection,
  LinkItem,
} from './catalog-base-schema';

const SITE_SECTION_TYPES = [
  'grid',
  'carousel',
  'comparison',
  'links',
  'hub',
] as const;
const PRODUCT_SHOWCASE_TYPES = ['grid', 'carousel', 'comparison'] as const;

type SiteSectionType = (typeof SITE_SECTION_TYPES)[number];
type ProductShowcaseType = (typeof PRODUCT_SHOWCASE_TYPES)[number];

export type ProductShowcase = Omit<CatalogSection, 'id' | 'order' | 'links'>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const asString = (value: unknown, fallback = '') =>
  typeof value === 'string' ? value : fallback;

const asOptionalString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value : null;

const asNumber = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asBoolean = (value: unknown, fallback: boolean) =>
  typeof value === 'boolean' ? value : fallback;

const byOrder = <T extends { order: number }>(items: T[]) =>
  [...items].sort((a, b) => a.order - b.order);

export const normalizeCatalogImages = (images: unknown): CatalogItem[] => {
  if (!Array.isArray(images)) return [];

  return byOrder(
    images
      .filter(isRecord)
      .map((image, index) => ({
        ...image,
        id: asString(image.id, `image-${index}`),
        url: asString(image.url),
        beforeUrl: asOptionalString(image.beforeUrl),
        caption: asOptionalString(image.caption),
        order: asNumber(image.order, index),
      }))
      .filter((image) => image.url || image.beforeUrl),
  ) as CatalogItem[];
};

export const normalizeCatalogLinks = (links: unknown): LinkItem[] => {
  if (!Array.isArray(links)) return [];

  return byOrder(
    links
      .filter(isRecord)
      .map((link, index) => ({
        ...link,
        id: asString(link.id, `link-${index}`),
        label: asString(link.label, 'Enlace'),
        url: asString(link.url),
        order: asNumber(link.order, index),
      }))
      .filter((link) => link.label.trim() && link.url.trim()),
  ) as LinkItem[];
};

const normalizeComparisonLabels = (labels: unknown) => {
  const source = isRecord(labels) ? labels : {};
  return {
    left: asString(source.left, 'Antes') || 'Antes',
    right: asString(source.right, 'Después') || 'Después',
  };
};

const normalizeSiteType = (type: unknown): SiteSectionType =>
  SITE_SECTION_TYPES.includes(type as SiteSectionType)
    ? (type as SiteSectionType)
    : 'grid';

const normalizeProductType = (type: unknown): ProductShowcaseType =>
  PRODUCT_SHOWCASE_TYPES.includes(type as ProductShowcaseType)
    ? (type as ProductShowcaseType)
    : 'carousel';

export const normalizeCatalogSection = (
  section: unknown,
  index = 0,
): CatalogSection => {
  const source = isRecord(section) ? section : {};
  const type = normalizeSiteType(source.type);

  return {
    ...source,
    id: asString(source.id, `section-${index}`),
    title: asString(source.title, 'Galería') || 'Galería',
    type,
    images: normalizeCatalogImages(source.images),
    links: normalizeCatalogLinks(source.links),
    isActive: asBoolean(source.isActive, true),
    order: asNumber(source.order, index),
    comparisonLabels: normalizeComparisonLabels(source.comparisonLabels),
  } as CatalogSection;
};

export const normalizeCatalogSections = (
  catalog: unknown,
): CatalogSection[] => {
  if (!Array.isArray(catalog)) return [];
  return byOrder(
    catalog.map((section, index) => normalizeCatalogSection(section, index)),
  ).map((section, index) => ({ ...section, order: index }));
};

export const normalizeProductShowcase = (
  showcase: unknown,
): ProductShowcase => {
  const source = isRecord(showcase) ? showcase : {};
  const { id: _id, order: _order, links: _links, ...rest } = source;

  return {
    ...rest,
    title: asString(source.title, 'Galería') || 'Galería',
    type: normalizeProductType(source.type),
    images: normalizeCatalogImages(source.images),
    isActive: asBoolean(source.isActive, true),
    comparisonLabels: normalizeComparisonLabels(source.comparisonLabels),
  } as ProductShowcase;
};

export const hasCatalogContent = (section: CatalogSection) =>
  section.isActive &&
  (section.images.some((image) => image.url || image.beforeUrl) ||
    ((section.type === 'links' || section.type === 'hub') &&
      section.links.length > 0));
